import { useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../ui/dialog';
import { X } from 'lucide-react';
import { useCreateSubscription } from '../../hooks/useSubscriptions';
import { useApi } from '../../hooks/useApi';
import { getClients } from '../../services/api';
import { BillingTerm, SubscriptionSource, PaginatedResponse, Client } from '../../types';
import toast from 'react-hot-toast';

interface ManualSubscriptionFormProps {
  onClose: () => void;
  onSuccess: () => void;
  preselectedClientId?: string;
}

export default function ManualSubscriptionForm({
  onClose,
  onSuccess,
  preselectedClientId,
}: ManualSubscriptionFormProps) {
  const fetchClients = useCallback(() => getClients({ pageSize: 1000 }), []);
  const { data: clientsData } = useApi<PaginatedResponse<Client>>(fetchClients, []);

  const [formData, setFormData] = useState({
    clientId: preselectedClientId || '',
    productName: '',
    sku: '',
    quantity: 1,
    billingTerm: 'annual' as BillingTerm,
    commitmentEndDate: '',
    renewalDate: '',
    autoRenew: true,
    source: 'manual' as SubscriptionSource,
    unitPrice: 0,
    totalPrice: 0,
  });

  const { createSubscription, loading, error } = useCreateSubscription();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.clientId) {
      toast.error('Please select a client');
      return;
    }

    try {
      await createSubscription({
        clientId: formData.clientId,
        productName: formData.productName,
        sku: formData.sku,
        quantity: formData.quantity,
        billingTerm: formData.billingTerm,
        commitmentEndDate: formData.commitmentEndDate,
        renewalDate: formData.renewalDate,
        autoRenew: formData.autoRenew,
        source: formData.source,
        unitPrice: formData.unitPrice,
        totalPrice: formData.totalPrice,
      });
      toast.success('Subscription created successfully');
      onSuccess();
    } catch (error) {
      toast.error('Failed to create subscription');
    }
  };

  // Auto-calculate total price
  const handleQuantityOrPriceChange = (field: 'quantity' | 'unitPrice', value: number) => {
    const newData = { ...formData, [field]: value };
    newData.totalPrice = newData.quantity * newData.unitPrice;
    setFormData(newData);
  };

  return (
    <Dialog open={true} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg p-6 [&>button]:hidden">
        <div className="flex items-center justify-between mb-4">
          <DialogTitle className="text-lg font-medium text-foreground">
            Add Manual Subscription
          </DialogTitle>
          <DialogDescription className="sr-only">Add a manually tracked subscription for a client</DialogDescription>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="label">Client *</label>
            <select
              value={formData.clientId}
              onChange={(e) => setFormData({ ...formData, clientId: e.target.value })}
              className="input"
              required
            >
              <option value="">Select a client...</option>
              {clientsData?.items.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.companyName}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="label">Product Name *</label>
              <input
                type="text"
                value={formData.productName}
                onChange={(e) => setFormData({ ...formData, productName: e.target.value })}
                className="input"
                placeholder="Microsoft 365 Business Premium"
                required
              />
            </div>

            <div>
              <label className="label">SKU</label>
              <input
                type="text"
                value={formData.sku}
                onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                className="input"
                placeholder="SPB"
              />
            </div>

            <div>
              <label className="label">Quantity *</label>
              <input
                type="number"
                value={formData.quantity}
                onChange={(e) => handleQuantityOrPriceChange('quantity', parseInt(e.target.value) || 0)}
                className="input"
                min="1"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Billing Term</label>
              <select
                value={formData.billingTerm}
                onChange={(e) => setFormData({ ...formData, billingTerm: e.target.value as BillingTerm })}
                className="input"
              >
                <option value="annual">Annual</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>

            <div>
              <label className="label">Source</label>
              <select
                value={formData.source}
                onChange={(e) => setFormData({ ...formData, source: e.target.value as SubscriptionSource })}
                className="input"
              >
                <option value="manual">Manual</option>
                <option value="direct">Direct (M365)</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Commitment End Date *</label>
              <input
                type="date"
                value={formData.commitmentEndDate}
                onChange={(e) => setFormData({ ...formData, commitmentEndDate: e.target.value })}
                className="input"
                required
              />
            </div>

            <div>
              <label className="label">Renewal Date *</label>
              <input
                type="date"
                value={formData.renewalDate}
                onChange={(e) => setFormData({ ...formData, renewalDate: e.target.value })}
                className="input"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="label">Unit Price</label>
              <input
                type="number"
                value={formData.unitPrice}
                onChange={(e) => handleQuantityOrPriceChange('unitPrice', parseFloat(e.target.value) || 0)}
                className="input"
                step="0.01"
                min="0"
              />
            </div>

            <div>
              <label className="label">Total Price</label>
              <input
                type="number"
                value={formData.totalPrice}
                onChange={(e) => setFormData({ ...formData, totalPrice: parseFloat(e.target.value) || 0 })}
                className="input"
                step="0.01"
                min="0"
              />
            </div>

            <div className="flex items-end">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.autoRenew}
                  onChange={(e) => setFormData({ ...formData, autoRenew: e.target.checked })}
                  className="h-4 w-4 rounded border-border text-primary-600 focus:ring-primary-500"
                />
                <span className="ml-2 text-sm text-muted-foreground">
                  Auto-Renew
                </span>
              </label>
            </div>
          </div>

          {error && (
            <div className="text-sm text-red-600 dark:text-red-400">{error}</div>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <button type="button" onClick={onClose} className="btn-secondary" disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Creating...' : 'Create Subscription'}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
